import React, { forwardRef } from 'react';
import {
  FormControl,
  FormLabel,
  InputBase,
  FormHelperText,
  Box,
  styled,
  Typography
} from '@mui/material';
import { StyledFormHelperText } from './StyledFormHelperText';

const StyledFormControl = styled(FormControl)(({ theme, error, variant = 'outlined' }) => ({
  width: '100%',
  fontFamily: 'Lato, sans-serif',
  
  '& .MuiFormLabel-root': {
    fontFamily: 'Lato, sans-serif',
    fontSize: '13px',
    fontWeight: 700,
    color: '#666666', // Text primary from Figma
    lineHeight: '20px',
    marginBottom: variant === 'standard' ? '4px' : '8px', // Less margin for standard
    transform: 'none',
    position: 'static',
    '&.Mui-focused': {
      color: '#666666',
    },
    '&.Mui-error': {
      color: '#666666',
    },
  },
}));

const StyledInputBase = styled(InputBase)(({ theme, error, variant = 'outlined', multiline }) => ({
  fontFamily: 'Lato, sans-serif',
  fontSize: '16',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#666666',
  padding: '8px',
  minHeight: multiline ? '80px' : '40px',
  display: 'flex',
  alignItems: 'center',
  
  // Variant-specific styles
  ...(variant === 'outlined' && {
    borderRadius: '8px',
    border: `1px solid ${error ? '#CC0000' : '#8C8C8C'}`,
    
    '&:hover': {
      borderColor: error ? '#CC0000' : '#666666',
    },
    
    '&.Mui-focused': {
      borderColor: error ? '#CC0000' : '#666666',
      outline: 'none',
      boxShadow: 'none',
    },
    
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5',
      borderColor: '#D0D0D0',
      color: '#999999',
    },
  }),
  
  ...(variant === 'standard' && {
    border: 'none',
    borderBottom: `1px solid ${error ? '#CC0000' : '#8C8C8C'}`,
    borderRadius: 0,
    // padding: '8px 0',
    padding: '2px 0px !important',
    backgroundColor: 'transparent',
    minHeight: '24px',
    '&.MuiInputBase-input':{
      padding: 0,
    },
    '&:hover': {
      borderBottomColor: error ? '#CC0000' : '#666666',
    },
    
    '&.Mui-focused': {
      borderBottomColor: error ? '#CC0000' : '#666666',
      borderBottomWidth: '1px',
      outline: 'none',
      boxShadow: 'none',
    },
    
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
      borderBottomColor: '#D0D0D0',
      color: '#999999',
    },
  }),

  
  '& .MuiInputBase-input': {
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',

    
  
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&[type=number]': {
      '-moz-appearance': 'textfield', // Firefox
    },
    '&::placeholder': {
      color: '#8C8C8C', // Placeholder color from Figma
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  },
  
  '& .MuiInputBase-inputMultiline': {
    resize: 'none',
    minHeight: multiline ? '64px' : 'auto', // Account for padding
  },
  
  '& .MuiInputAdornment-root': {
    color: '#666666',
    marginRight: '8px',
    '&.MuiInputAdornment-positionStart': {
      marginRight: '8px',
      marginLeft: 0,
    },
    '&.MuiInputAdornment-positionEnd': {
      marginLeft: '8px',
      marginRight: 0,
    },
  },
}));

// const StyledFormHelperText = styled(FormHelperText)(({ theme, error }) => ({
//   fontFamily: 'Lato, sans-serif',
//   fontSize: '13px',
//   fontWeight: 400,
//   lineHeight: '20px',
//   color: error ? '#CC0000' : '#666666',
//   marginTop: '8px',
//   marginLeft: 0,
//   marginRight: 0,
//   '&.Mui-error':{
//     color: '#CC0000',
//   },
// }));

const StyledDescription = styled(Box)(({ theme }) => ({
  fontFamily: 'Lato, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#666666',
  marginTop: '8px',
}));

const TextField = forwardRef(({
  label,
  description,
  helperText,
  error = false,
  variant = 'outlined',
  multiline = false,
  rows,
  placeholder,
  value,
  onChange,
  disabled = false,
  required = false,
  fullWidth = true,
  sx,
  slotProps,
  fetchedState = false,
  ...props
}, ref) => {
  return (
    <StyledFormControl 
      error={error} 
      variant={variant}
      fullWidth={fullWidth}
      sx={{ ...sx, gap: '2px' }}
     
    >
      {label && (
        <Typography sx={{ color: error ? '#CC0000' : '#666', fontSize: '13px', lineHeight: '20px', fontWeight: '700' }} component="legend" required={required}>
          {label}
        </Typography>
      )}
      
      {description && (
        <StyledDescription>
          {description}
        </StyledDescription>
      )}
      {fetchedState && (
        <Typography sx={{ fontSize: '16px !important', lineHeight: '24px !important', fontWeight: '400' }}>
          {value}
        </Typography>
      )}
      {!fetchedState && (
        <StyledInputBase
          className='default-input'
          ref={ref}
          error={error}
          variant={variant}
          multiline={multiline}
          rows={multiline ? (rows || 4) : undefined}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          startAdornment={slotProps?.input?.startAdornment}
          endAdornment={slotProps?.input?.endAdornment}
        
          {...props}
        />
      )}
        
        {helperText && (
          <StyledFormHelperText error={error}>
            {helperText}
          </StyledFormHelperText>
        )}
      
    </StyledFormControl>
  );
});
export default TextField;
